import { Global, ThemeProvider } from '@emotion/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import GlobalApiHandler from './components/common/GlobalApiHandler';
import SessionHandler from './components/common/SessionHandler.tsx';
import { globalStyle } from './emotionStyles.ts';
import { useThemeFromRedux } from './hooks/useThemeFromRedux.ts';
import { useBlockLanguage } from './hooks/useBlockLanguage.ts';
import AdminPage from './pages/AdminPage.tsx';
import { CameraTestPage } from './pages/CameraTestPage';
import { ChooseFramePage } from './pages/ChooseFramePage';
import { ChoosePhotoPage } from './pages/ChoosePhotoPage';
import { CopyPage } from './pages/CopyPage';
import { OtpPage } from './pages/OtpPage';
import { PaymentPage } from './pages/PaymentPage';
import { PrintPage } from './pages/PrintPage';
import QrcodePage from './pages/QrcodePage.tsx';
import { ShotPage } from './pages/ShotPage';
import { StandbyPage } from './pages/StandbyPage';
import { StartPage } from './pages/StartPage';
import { ThemePage } from './pages/ThemePage';
import { checkCert, getMainBlock, reissueToken } from './service/api.v2.service/auth.ts';
import { setBlock } from './store/blockSlice';
import { RootState } from './store/store';
import { getSubFolder, isMainBlock } from './utils/util';
import { reportTokenDebug, reportTokenWarning } from './utils/errorReporting';

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { block } = useSelector((state: RootState) => state.block);

  useBlockLanguage({ fellin: 'en', ces: 'en', sfpoc: 'en' });

  const isTokenValid = (): boolean => {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;
    return true;
  };

  const initSession = async () => {
    // Sentry로 localStorage 상태 전송
    reportTokenDebug('App 초기화 - localStorage 상태', {
      accessToken: localStorage.getItem('accessToken')
        ? `${localStorage.getItem('accessToken')!.substring(0, 10)}...`
        : 'NULL/UNDEFINED',
      refreshToken: localStorage.getItem('refreshToken')
        ? `${localStorage.getItem('refreshToken')!.substring(0, 10)}...`
        : 'NULL/UNDEFINED',
      isLoggedIn: localStorage.getItem('isLoggedIn'),
      pathname: location.pathname,
    });

    const path = location.pathname;
    const isOtpOrQr = path === '/otp' || path === '/qrcode';
    const isMainAndNotOtpOrQr = isMainBlock(path) && !isOtpOrQr;

    if (isMainAndNotOtpOrQr) {
      window.location.href = '/otp';
      return;
    }

    try {
      if (!isTokenValid()) {
        reportTokenWarning('토큰이 유효하지 않아 reissue 시도', {
          pathname: path,
          hasAccessToken: !!localStorage.getItem('accessToken'),
          hasRefreshToken: !!localStorage.getItem('refreshToken'),
        });
        await reissueToken();
      } else {
        reportTokenDebug('토큰이 유효함', {
          pathname: path,
        });
      }

      reportTokenDebug('checkCert 호출 전 localStorage 상태', {
        accessToken: localStorage.getItem('accessToken')
          ? `${localStorage.getItem('accessToken')!.substring(0, 10)}...`
          : 'NULL/UNDEFINED',
        pathname: path,
      });

      const block = await checkCert();
      dispatch(setBlock(block));

      if (getSubFolder(path) !== block.subFolder) {
        navigate(`/${block.subFolder}`);
      }
    } catch {
      if (isOtpOrQr) {
        const block = await getMainBlock();
        dispatch(setBlock({ ...block, subFolder: '' }));
      } else {
        window.location.href = '/otp';
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initSession();
  }, []);

  if (isLoading) {
    return <></>;
  }

  console.log('Current routing state:', {
    pathname: location.pathname,
    blockSubFolder: block?.subFolder,
    currentUrl: window.location.href,
  });

  return (
    <ThemeWrapper>
      <GlobalApiHandler />
      <SessionHandler>
        <Routes>
          <Route path="/otp" element={<OtpPage />} />
          <Route path="/qrcode" element={<QrcodePage />} />
          <Route path={`/${block?.subFolder || ''}`} element={<Outlet />}>
            <Route index element={<Navigate to="start" replace />} />
            <Route path="start" element={<StartPage />} />
            <Route path="copy" element={<CopyPage />} />
            <Route path="payment" element={<PaymentPage />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="camera-test" element={<CameraTestPage />} />
            <Route
              path="standby"
              element={
                <ProtectedRoute>
                  <StandbyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="shot"
              element={
                <ProtectedRoute>
                  <ShotPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="theme"
              element={
                <ProtectedRoute>
                  <ThemePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="chooseframe"
              element={
                <ProtectedRoute>
                  <ChooseFramePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="choosephoto"
              element={
                <ProtectedRoute>
                  <ChoosePhotoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="print"
              element={
                <ProtectedRoute>
                  <PrintPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="start" replace />} />
          </Route>
        </Routes>
      </SessionHandler>
    </ThemeWrapper>
  );
}

const ThemeWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const theme = useThemeFromRedux();

  if (!theme) {
    return <></>;
  }

  return (
    <ThemeProvider theme={theme}>
      <Global styles={globalStyle} />
      {children}
    </ThemeProvider>
  );
};

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  const isPaid = useSelector((state: RootState) => state.auth.isPaid);
  const location = useLocation();
  const isPaymentRequired = useSelector((state: RootState) => state.block.block?.inAppPay);

  if (!isLoggedIn) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  if (isPaymentRequired && !isPaid) {
    return <Navigate to="/payment" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const AppWrapper = () => (
  <Router>
    <App />
  </Router>
);

export default AppWrapper;

// NOTE: 로그인 상태 체크
// useEffect(() => {
//   const checkLoginStatus = () => {
//     const isLoggedIn = getLoginState();
//     dispatch(setLoggedIn(isLoggedIn));
//     setIsLoading(false);
//   };

//   window.addEventListener("storage", checkLoginStatus);
//   checkLoginStatus();

//   return () => {
//     window.removeEventListener("storage", checkLoginStatus);
//   };
// }, [dispatch]);
